import Divider from "@mui/material/Divider";
import useMediaQuery from "@mui/material/useMediaQuery";
import parse from "html-react-parser";
import Link from "next/link";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import Color from "../../../../assets/colors";
import { getLastActiveAtTime } from "../../../../helpers/conditional_rendering";
import SingleBenefitBox from "./SingleJobBenefit/SingleBenefitBox";
import {
  AdditionalInfoBody,
  AdditionalInfoTitle,
  ChipStyled,
  DivStyled,
  LabelStyled,
  SectionContainer,
  SectionTitle,
} from "./styles";

function SingleJobDescription(props) {
  const { job, handleHref } = props;
  const { t } = useTranslation();

  const getCareerLevelLabel = (careerLevelValue) => {
    if (!careerLevelValue) return "";
    const key = careerLevelValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`jobs.single_job.career_level_labels.${key}`, {
      defaultValue: careerLevelValue,
    });
  };

  const getSpecialisationLabel = (specialisationValue) => {
    if (!specialisationValue) return "";
    const key = specialisationValue
      .toString()
      .toLowerCase()
      .replace(/&/g, "and")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return t(`companydetail.specialisation_labels.${key}`, {
      defaultValue: specialisationValue,
    });
  };

  const careerLevelLabel = getCareerLevelLabel(job?.careerLevel);

  const { benefits } = job.company || {};

  const belowTablet = useMediaQuery("(max-width: 767px)");

  return (
    <Fragment>
      {/* JOB DESCRIPTION SECTION */}
      <SectionContainer aria-label="job-description-section">
        <SectionTitle variant="h1">
          {t("jobs.single_job.description")}
        </SectionTitle>
        {parse(job?.description)}
      </SectionContainer>
      <Divider style={{ margin: "30px 0px" }} />

      {/* JOB REQUIREMENT SECTION */}
      {job?.requirements ? (
        <Fragment>
          <SectionContainer aria-label="job-requirement-section">
            <SectionTitle variant="h1">
              {t("jobs.single_job.requirements")}
            </SectionTitle>
            {parse(job?.requirements)}
          </SectionContainer>
          <Divider style={{ margin: "30px 0px" }} />
        </Fragment>
      ) : null}

      {/* WORK ARRANGEMENT SECTION */}
      {job && job?.globalHirePreferences.enabled ? (
        <div id="job-work-arrangement">
          <SectionContainer aria-label="right-to-work-section">
            <SectionTitle variant="h1">
              {t("jobs.single_job.right_to_work_title")}
            </SectionTitle>

            <ul>
              {job?.globalHirePreferences.rightToWorkNoPreference ? (
                <li>{t("jobs.single_job.right_to_work_no_preference")}</li>
              ) : null}
              {job?.globalHirePreferences.rightToWorkOpenToSponsor ? (
                <li>{t("jobs.single_job.right_to_work_sponsor")}</li>
              ) : null}
              {job?.globalHirePreferences.rightToWorkLocalCitizen ||
              job?.globalHirePreferences.rightToWorkPermanentCitizen ||
              job?.globalHirePreferences.rightToWorkWorkVisaOrEmploymentPass ? (
                <>
                  <li>{t("jobs.single_job.right_to_work_preferred")}</li>
                  <ul type="disc">
                    {job?.globalHirePreferences.rightToWorkLocalCitizen ? (
                      <li>{t("jobs.single_job.right_to_work_local")}</li>
                    ) : null}
                    {job?.globalHirePreferences.rightToWorkPermanentCitizen ? (
                      <li>{t("jobs.single_job.right_to_work_pr")}</li>
                    ) : null}
                    {job?.globalHirePreferences
                      .rightToWorkWorkVisaOrEmploymentPass ? (
                      <li>{t("jobs.single_job.right_to_work_permit")}</li>
                    ) : null}
                  </ul>
                </>
              ) : null}
            </ul>
          </SectionContainer>

          <SectionContainer aria-label="work-arrangement-section">
            <SectionTitle variant="h1">
              {t("jobs.single_job.working_arrangement")}
            </SectionTitle>

            <ul>
              {job?.globalHirePreferences.workingArrangementRemote ? (
                <li>{t("jobs.single_job.arrangement_remote")}</li>
              ) : null}
              {job?.globalHirePreferences.workingArrangementOnSite ? (
                <li>{t("jobs.single_job.arrangement_onsite")}</li>
              ) : null}
              {job?.globalHirePreferences.workingArrangementHybrid ? (
                <li>{t("jobs.single_job.arrangement_hybrid")}</li>
              ) : null}
            </ul>
          </SectionContainer>
          <Divider style={{ margin: "30px 0px" }} />
        </div>
      ) : null}

      {/* JOB SKILL SECTION */}
      {Array.isArray(job?.skills) && job?.skills.length > 0 ? (
        <Fragment>
          <SectionContainer aria-label="job-skill-requirement-section">
            <SectionTitle variant="h1">
              {t("jobs.single_job.skills")}
            </SectionTitle>
            <DivStyled container flexDirection={"row"}>
              {job?.skills.map((skill, index) => (
                <ChipStyled label={skill.name} key={`job-skill-${index}`} />
              ))}
            </DivStyled>
          </SectionContainer>
          <Divider style={{ margin: "30px 0px" }} />
        </Fragment>
      ) : null}

      {/* COMPANY BENEFIT SECTION */}
      {Array.isArray(benefits) && benefits.length > 0 ? (
        <Fragment>
          <section aria-label="job-benefit-section">
            <SectionTitle variant="h1">
              {t("jobs.single_job.company_benefits")}
            </SectionTitle>
            <DivStyled
              display={belowTablet ? "flex" : "grid"}
              gridTemplateColumns={"repeat(3, 1fr)"}
              gap={"15px"}
              sx={{
                overflowX: belowTablet ? "scroll" : null,
              }}
            >
              {benefits.map((benefit) => (
                <SingleBenefitBox
                  key={`job-benefit-${benefit.title}`}
                  jobBenefit={benefit}
                />
              ))}
            </DivStyled>
          </section>
          <Divider style={{ margin: "30px 0px" }} />
        </Fragment>
      ) : null}

      {/* JOB ADDITIONAL INFO SECTION */}
      <section aria-label="job-additional-info-section">
        <SectionTitle variant="h1">
          {t("jobs.single_job.additional_info")}
        </SectionTitle>
        <DivStyled container columns={12} gap={"15px"}>
          {getLastActiveAtTime(job?.company?.lastActiveAt, t) !== null ? (
            <DivStyled item xs={8} tablet={4}>
              <AdditionalInfoTitle variant="h2">
                {t("jobs.single_job.company_activity")}
              </AdditionalInfoTitle>
              <AdditionalInfoBody variant="body1">
                {t("jobs.single_job.last_active", {
                  time: getLastActiveAtTime(job?.company?.lastActiveAt, t),
                })}
              </AdditionalInfoBody>
            </DivStyled>
          ) : null}

          {job?.jobType.toString().toLowerCase().includes("internship") ||
          (job.minYearsExperience <= 0 && job.maxYearsExperience === 0) ? (
            <DivStyled item xs={8} tablet={4}>
              <AdditionalInfoTitle variant="h2">
                {t("jobs.single_job.experience_level")}
              </AdditionalInfoTitle>
              <Link
                href={handleHref("experience", job)}
                style={{ textDecoration: "none", color: "currentcolor" }}
              >
                <DivStyled
                  style={{
                    background: Color.lightBlue,
                    padding: "0.25rem 0.5rem",
                    borderRadius: "0.25rem",
                    color: Color.blue,
                    cursor: "pointer",
                    width: "fit-content",
                  }}
                >
                  <LabelStyled
                    variant="body1"
                    fontWeight={"bold"}
                    fontSize={"14px"}
                    sx={{
                      color: Color.blue,
                    }}
                  >
                    {t("jobs.single_job.no_experience_needed")}
                  </LabelStyled>
                </DivStyled>
              </Link>
            </DivStyled>
          ) : job.minYearsExperience === null ||
            job.maxYearsExperience === null ? null : (
            <DivStyled item xs={8} tablet={4}>
              <AdditionalInfoTitle variant="h2">
                {t("jobs.single_job.experience_level")}
              </AdditionalInfoTitle>
              <Link
                href={handleHref("experience", job)}
                style={{ textDecoration: "none", color: "currentcolor" }}
              >
                <DivStyled
                  style={{
                    background: Color.lightBlue,
                    padding: "0.25rem 0.5rem",
                    borderRadius: "0.25rem",
                    color: Color.blue,
                    cursor: "pointer",
                    width: "fit-content",
                  }}
                >
                  <LabelStyled
                    variant="body1"
                    fontWeight={"bold"}
                    fontSize={"14px"}
                    sx={{
                      color: Color.blue,
                    }}
                  >
                    {t("jobs.single_job.years_experience", {
                      min: job?.minYearsExperience,
                      max: job?.maxYearsExperience,
                    })}
                  </LabelStyled>
                </DivStyled>
              </Link>
            </DivStyled>
          )}

          {careerLevelLabel ? (
            <DivStyled item xs={8} tablet={3}>
              <AdditionalInfoTitle variant="h2">
                {t("jobs.single_job.career_level")}
              </AdditionalInfoTitle>
              <AdditionalInfoBody variant="body1">
                {careerLevelLabel}
              </AdditionalInfoBody>
            </DivStyled>
          ) : null}

          {Array.isArray(job?.tracks) && job?.tracks.length > 0 ? (
            <DivStyled item xs={8} tablet={4}>
              <AdditionalInfoTitle variant="h2">
                {t("jobs.single_job.job_specialisation")}
              </AdditionalInfoTitle>

              <DivStyled
                container
                flexDirection={"row"}
                gap={"8px"}
                flexWrap={"wrap"}
              >
                {job?.tracks?.map((track, index) => (
                  <Link
                    key={`track-${index}`}
                    href={handleHref("specialisation", job)}
                    style={{
                      textDecoration: "none",
                      color: "currentcolor",
                      display: "inline-block",
                    }}
                  >
                    <DivStyled
                      style={{
                        background: Color.lightBlue,
                        padding: "0.25rem 0.5rem",
                        borderRadius: "0.25rem",
                        color: Color.blue,
                        cursor: "pointer",
                        display: "inline-block",
                      }}
                    >
                      <LabelStyled
                        variant="body1"
                        fontWeight={"bold"}
                        fontSize={"14px"}
                        sx={{
                          color: Color.blue,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {getSpecialisationLabel(track.title)}
                      </LabelStyled>
                    </DivStyled>
                  </Link>
                ))}
              </DivStyled>
            </DivStyled>
          ) : null}
        </DivStyled>
      </section>
    </Fragment>
  );
}

export default SingleJobDescription;
